import React, { Suspense, useState } from 'react';

import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';

import { css } from 'emotion';

import styled from '@emotion/styled';

import Header from './Header';
import DriverDetails from './DriverDetails';

import useTheme from '@vromo/hooks/useTheme';

import useStylesheets from '@vromo/hooks/useStylesheets';

import smoothscroll from 'smoothscroll-polyfill';

import Widget from './Widget';

import SpatulaSignedJobView from './SpatulaSignedJobView.css';
import TrackingLinkStore from '../TrackingLinkStore';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import { Theme } from '@emotion/react';
import Button from '@vromo/ui/components/Button';
import styles from '../trackingLink.style';
import { LocalStorage } from '@vromo/models';

const Map2 = React.lazy(() => import(/* webpackPrefetch: true */ './Map2.lazy'));

smoothscroll.polyfill();

const Container = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;

    font-family: aktiv-grotesk, Helvetica, Open Sans, sans-serif;
    background: ${({ theme }) => theme.background};
    overflow: hidden;
    overflow-y: auto;
`;

const bannerText =
    'This website uses cookies. By clicking "Accept", you agree to the use of cookies to analyze site traffic. For more information, please see our Privacy Policy.';

/** default tracking link page, than renders all the widgets and the map */
export default observer<{ host: string; auth: string }>(function WeDispatch({ host, auth }) {
    useStylesheets(SpatulaSignedJobView);

    const campaignTheme = useTheme();
    const rootStore = useStore<TrackingLinkStore>('rootStore');
    // parent comp has verified that there is a job and a vehicle
    const job = rootStore.jobs.first()!;
    const vehicle = rootStore.vehicles.first()!;
    const { widgets = [] } = job.campaign || {};

    const [showBanner, setShowBanner] = useState<boolean>(!LocalStorage.hideTrackingCookieBanner);

    return (
        <Container>
            <Header />
            <div
                className={css`
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;

                    @media (min-width: 1000px) {
                        flex-direction: row;
                    }
                `}>
                <div style={{ flex: 1, minHeight: 200 }}>
                    <ObservableAutoSizer
                        customChildren={({ width, height }) =>
                            width && height ? (
                                <Suspense
                                    fallback={
                                        <div
                                            style={{ width, height, backgroundColor: '#F3F3F3' }}
                                        />
                                    }>
                                    <Map2
                                        icon={job.campaign?.theme?.icon?.url ?? undefined}
                                        jobs={rootStore.jobs}
                                        vehicles={rootStore.vehicles}
                                        width={width}
                                        height={height}
                                    />
                                </Suspense>
                            ) : null
                        }
                    />
                    {showBanner && (
                        <div style={styles.cookieBanner}>
                            <div style={styles.cookieText}>{bannerText}</div>
                            <Button
                                style={styles.cookieButton}
                                onClick={() => {
                                    setShowBanner(false);
                                    LocalStorage.hideTrackingCookieBanner = true;
                                }}>
                                Accept
                            </Button>
                        </div>
                    )}
                </div>

                <div
                    className={css`
                        display: flex;
                        flex-direction: column;
                        max-height: 70vh;

                        @media (min-width: 1000px) {
                            max-height: none;
                            width: 400px;
                        }
                    `}>
                    <div
                        className={css`
                            display: flex;
                            flex-direction: column;
                            -webkit-overflow-scrolling: touch;
                            min-height: 0;

                            @media (min-width: 1000px) {
                                flex: 1;
                                flex-direction: column-reverse;
                            }
                        `}>
                        {vehicle && (
                            <div
                                className={css`
                                    display: flex;
                                    flex-direction: column;
                                    align-items: center;
                                    flex-shrink: 0;
                                `}>
                                <DriverDetails
                                    vehicle={vehicle}
                                    job={job}
                                    host={host}
                                    auth={auth}
                                />
                            </div>
                        )}
                        {job.campaign?.promotion?.enabled && (
                            <div
                                className={css`
                                    width: 100%;
                                    max-width: 400px;
                                    margin-left: auto;
                                    margin-right: auto;

                                    @media (min-width: 1000px) {
                                        flex: 1;
                                        overflow-y: auto;
                                    }
                                `}>
                                {widgets.map((widget) => (
                                    <Widget
                                        key={widget.id}
                                        widget={widget}
                                        theme={campaignTheme as Theme}
                                        vehicle={vehicle}
                                        job={job}
                                        host={host}
                                        auth={auth}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Container>
    );
});
